import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Heart, Leaf } from 'lucide-react';
import Layout from '../components/Layout';
import GuideCard from '../components/GuideCard';

const featuredGuides = [
  {
    title: "Morning Skincare Routine",
    subtitle: "Start your day with a simple, effective routine that protects and nourishes your skin.",
    category: "Skincare",
    link: "/skincare/morning-routines",
    imageUrl: "https://customer-assets.emergentagent.com/job_beauty-radar-1/artifacts/2lh9egs8_morning-skincare.jpg.png"
  },
  {
    title: "Minimal Makeup Guide",
    subtitle: "Achieve a natural, polished look with just a few essential products.",
    category: "Makeup",
    link: "/makeup/minimal",
    imageUrl: "https://customer-assets.emergentagent.com/job_beauty-radar-1/artifacts/oqoaxfdo_minimal-makeup.jpg.png"
  },
  {
    title: "Self-Care Essentials",
    subtitle: "Products and tools for relaxation, stress relief, and daily self-care rituals.",
    category: "Wellness",
    link: "/wellness/self-care-essentials",
    imageUrl: "https://customer-assets.emergentagent.com/job_beauty-radar-1/artifacts/usvliotd_self-care-essentials.jpg.png"
  }
];

const categories = [
  {
    title: "Skincare",
    description: "Routines, treatments, and product recommendations for every skin type and concern.",
    icon: Sparkles,
    link: "/skincare",
    color: "bg-[#F2EBE3]"
  },
  {
    title: "Makeup",
    description: "From everyday essentials to minimal looks, find your perfect makeup routine.",
    icon: Heart,
    link: "/makeup",
    color: "bg-[#F5F5F0]"
  },
  {
    title: "Wellness",
    description: "Supplements, self-care essentials, and daily health picks for holistic wellbeing.",
    icon: Leaf,
    link: "/wellness",
    color: "bg-[#F2EBE3]"
  }
];

const Home = () => {
  return (
    <Layout>
      {/* Hero Section - Redesigned for horizontal image */}
      <section data-testid="hero-section" className="relative bg-[#FDFCF8]">
        {/* Hero Image - Full width horizontal */}
        <div className="w-full bg-[#F2EBE3]">
          <div className="max-w-7xl mx-auto">
            <div className="aspect-[21/9] md:aspect-[3/1] lg:aspect-[21/9] overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_beauty-radar-1/artifacts/q93a06dc_home-hero.jpg.png" 
                alt="Four diverse women representing inclusive beauty and wellness"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
        </div>
        
        {/* Hero Content - Below image */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="max-w-3xl mx-auto text-center animate-fadeIn">
            <p className="text-xs tracking-widest uppercase font-medium text-[#C88D74] mb-4">
              Honest Recommendations
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#1C1C1C] leading-tight mb-6">
              Curated guides for 
              <span className="italic font-normal"> thoughtful</span> beauty & wellness
            </h1>
            <p className="text-base md:text-lg text-[#374151] leading-relaxed mb-8 max-w-2xl mx-auto">
              No fluff, no hype. Just honest, research-backed product recommendations to help you build routines that actually work for your skin and lifestyle.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/skincare"
                data-testid="hero-cta-skincare"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#3A4D39] text-white text-sm font-medium tracking-wide uppercase transition-all duration-300 hover:bg-[#2A3D29]"
              >
                Explore Skincare
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link 
                to="/about"
                data-testid="hero-cta-about"
                className="inline-flex items-center gap-2 px-6 py-3 border border-[#3A4D39] text-[#3A4D39] text-sm font-medium tracking-wide uppercase transition-all duration-300 hover:bg-[#3A4D39] hover:text-white"
              >
                About Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section data-testid="categories-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-xs tracking-widest uppercase font-medium text-[#C88D74] mb-3">
              Browse by Category
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#1C1C1C]">
              Find what you need
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {categories.map((category, index) => (
              <Link 
                key={category.title}
                to={category.link}
                data-testid={`category-${category.title.toLowerCase()}`}
                className={`group p-8 md:p-10 ${category.color} border border-[#E5E5E5] transition-all duration-300 hover:shadow-lg hover:border-[#C88D74]/30 animate-fadeIn stagger-${index + 1}`}
              >
                <category.icon className="w-8 h-8 text-[#3A4D39] mb-4" />
                <h3 className="font-heading text-xl md:text-2xl font-semibold text-[#1C1C1C] mb-2 group-hover:text-[#3A4D39] transition-colors duration-300">
                  {category.title}
                </h3>
                <p className="text-sm text-[#374151] mb-4">
                  {category.description}
                </p>
                <span className="inline-flex items-center gap-2 text-[#3A4D39] text-sm font-medium tracking-wide uppercase">
                  Explore
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Guides Section */}
      <section data-testid="featured-guides-section" className="py-20 md:py-32 bg-[#F5F5F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 md:mb-16">
            <div>
              <p className="text-xs tracking-widest uppercase font-medium text-[#C88D74] mb-3">
                Start Here
              </p>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#1C1C1C]">
                Popular Guides
              </h2>
            </div>
            <Link 
              to="/skincare"
              className="mt-4 md:mt-0 inline-flex items-center gap-2 text-[#3A4D39] text-sm font-medium tracking-wide uppercase hover:underline"
            >
              View All Guides
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {featuredGuides.map((guide, index) => (
              <div key={guide.title} className={`animate-fadeIn stagger-${index + 1}`}>
                <GuideCard {...guide} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section data-testid="trust-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#1C1C1C] mb-6">
            Why trust our recommendations?
          </h2>
          <p className="text-base md:text-lg text-[#374151] leading-relaxed mb-8">
            We take a calm, research-informed approach to product recommendations. No sponsored content, no pressure tactics—just honest picks based on ingredients, reviews, and real-world results.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
            <div className="p-6 bg-[#F5F5F0]">
              <h4 className="font-heading text-lg font-semibold text-[#1C1C1C] mb-2">Research-Backed</h4>
              <p className="text-sm text-[#374151]">Every recommendation is based on ingredient analysis and user reviews.</p>
            </div>
            <div className="p-6 bg-[#F2EBE3]">
              <h4 className="font-heading text-lg font-semibold text-[#1C1C1C] mb-2">Transparent</h4>
              <p className="text-sm text-[#374151]">We clearly disclose affiliate relationships. Your trust matters most.</p>
            </div>
            <div className="p-6 bg-[#F5F5F0]">
              <h4 className="font-heading text-lg font-semibold text-[#1C1C1C] mb-2">No Pressure</h4>
              <p className="text-sm text-[#374151]">Our guides educate first. Buy only what feels right for you.</p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Home;
